import {useCallback, useEffect, useRef, useState} from "react";

const IDLE_TIMEOUT_MS = 60 * 45 * 1000;
const WARNING_DURATION_MS = 45 * 1000;

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
    "mousedown",
    "keydown",
    "click",
];

type UseIdleLogoutProps = {
    isAuthenticated: boolean;
    onLogout: () => void;
    enabled?: boolean;
};

export default function useIdleLogout({
                                          isAuthenticated,
                                          onLogout,
                                          enabled = true,
                                      }: UseIdleLogoutProps) {
    const logoutTimeoutRef = useRef<number | null>(null);
    const warningTimeoutRef = useRef<number | null>(null);
    const countdownIntervalRef = useRef<number | null>(null);
    const showWarningRef = useRef(false);

    const onLogoutRef = useRef(onLogout);
    const isAuthenticatedRef = useRef(isAuthenticated);
    const enabledRef = useRef(enabled);

    const [showWarning, setShowWarning] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);

    useEffect(() => {
        onLogoutRef.current = onLogout;
    }, [onLogout]);

    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    useEffect(() => {
        enabledRef.current = enabled;
    }, [enabled]);

    const clearAllTimers = useCallback(() => {
        if (logoutTimeoutRef.current) {
            window.clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
        }

        if (warningTimeoutRef.current) {
            window.clearTimeout(warningTimeoutRef.current);
            warningTimeoutRef.current = null;
        }

        if (countdownIntervalRef.current) {
            window.clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    }, []);

    const resetTimer = useCallback(() => {
        if (!enabledRef.current || !isAuthenticatedRef.current) return;

        clearAllTimers();

        const effectiveWarningMs = Math.min(WARNING_DURATION_MS, IDLE_TIMEOUT_MS);
        const warningStartMs = Math.max(IDLE_TIMEOUT_MS - effectiveWarningMs, 0);

        showWarningRef.current = false;
        setShowWarning(false);
        setSecondsRemaining(Math.ceil(effectiveWarningMs / 1000));

        warningTimeoutRef.current = window.setTimeout(() => {
            if (!enabledRef.current || !isAuthenticatedRef.current) return;

            showWarningRef.current = true;
            setShowWarning(true);
            setSecondsRemaining(Math.ceil(effectiveWarningMs / 1000));

            const warningEndsAt = Date.now() + effectiveWarningMs;

            countdownIntervalRef.current = window.setInterval(() => {
                if (!enabledRef.current || !isAuthenticatedRef.current) return;

                const remaining = Math.max(
                    0,
                    Math.ceil((warningEndsAt - Date.now()) / 1000)
                );

                setSecondsRemaining(remaining);
            }, 500);
        }, warningStartMs);

        logoutTimeoutRef.current = window.setTimeout(() => {
            if (!enabledRef.current || !isAuthenticatedRef.current) return;

            clearAllTimers();
            showWarningRef.current = false;
            setShowWarning(false);
            setSecondsRemaining(0);
            onLogoutRef.current();
        }, IDLE_TIMEOUT_MS);
    }, [clearAllTimers]);

    const stayLoggedIn = useCallback(() => {
        if (!enabledRef.current || !isAuthenticatedRef.current) return;
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!enabled || !isAuthenticated) {
            clearAllTimers();
            showWarningRef.current = false;
            setShowWarning(false);
            setSecondsRemaining(0);
            return;
        }

        const handleActivity = () => {
            if (!showWarningRef.current) {
                resetTimer();
            }
        };

        ACTIVITY_EVENTS.forEach((event) => {
            window.addEventListener(event, handleActivity, {passive: true});
        });

        resetTimer();

        return () => {
            ACTIVITY_EVENTS.forEach((event) => {
                window.removeEventListener(event, handleActivity);
            });

            clearAllTimers();
        };
    }, [enabled, isAuthenticated, clearAllTimers, resetTimer]);

    return {
        showWarning,
        secondsRemaining,
        stayLoggedIn,
    };
}